import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import styles from './wave-loader.module.scss';

interface WaveLoaderProps {
  size?: number;
  className?: string;
  waveSpeed?: number;
  meshSpeed?: number;
  waveOpacity?: number;
  waveBlur?: number;
  meshBlur?: number;
  bobbingSpeed?: number;
  bobbingIntensity?: number;
  numPoints?: number;
}

interface Point {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
}

export const WaveLoader = ({
  size = 16,
  className,
  waveSpeed = 3,
  meshSpeed = 5,
  waveOpacity = 0.75,
  waveBlur = 8,
  meshBlur = 10,
  bobbingSpeed = 0.5,
  bobbingIntensity = 0.03,
  numPoints = 3,
}: WaveLoaderProps) => {
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);
  const meshCanvasRef = useRef<HTMLCanvasElement>(null);
  const loaderContainerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const waveCanvas = waveCanvasRef.current;
    const meshCanvas = meshCanvasRef.current;
    const loaderContainer = loaderContainerRef.current;

    if (!waveCanvas || !meshCanvas || !loaderContainer) {
      return;
    }

    const waveCtx = waveCanvas.getContext('2d');
    const meshCtx = meshCanvas.getContext('2d');

    if (!waveCtx || !meshCtx) {
      return;
    }

    const baseReferenceSize = 150; // Reference size for scaling calculations
    const scale = size / baseReferenceSize;
    let time = 0;
    let bobbingTime = 0;

    waveCanvas.width = size;
    waveCanvas.height = size;
    meshCanvas.width = size;
    meshCanvas.height = size;

    const seed = Math.floor(Math.random() * 100000);

    function seededRandom(s: number): number {
      const x = Math.sin(s) * 10000;
      return x - Math.floor(x);
    }

    let randomSeed = seed;
    function nextRandom(): number {
      return seededRandom(randomSeed++);
    }

    const colors = [
      Math.floor(nextRandom() * 360),
      Math.floor(nextRandom() * 360),
      Math.floor(nextRandom() * 360),
    ];

    const waveColors = [
      `hsl(${Math.floor(nextRandom() * 360)}, 70%, 60%)`,
      `hsl(${Math.floor(nextRandom() * 360)}, 70%, 60%)`,
    ];

    // Set static CSS properties once
    loaderContainer!.style.width = `${size}px`;
    loaderContainer!.style.height = `${size}px`;
    loaderContainer!.style.transformOrigin = 'center';
    waveCanvas!.style.opacity = `${waveOpacity}`;
    waveCanvas!.style.filter = `blur(${waveBlur * scale}px)`;
    meshCanvas!.style.filter = `blur(${meshBlur * scale}px)`;

    function updateBobbingTransform() {
      const bobbingOffset = Math.sin(bobbingTime * bobbingSpeed) * bobbingIntensity;
      const bobbingScale = 1 + bobbingOffset;
      loaderContainer!.style.transform = `scale(${bobbingScale})`;
    }

    const points: Point[] = [];

    for (let i = 0; i < numPoints; i++) {
      points.push({
        x: nextRandom() * size,
        y: nextRandom() * size,
        vx: (nextRandom() - 0.5) * meshSpeed * scale,
        vy: (nextRandom() - 0.5) * meshSpeed * scale,
        color: `hsl(${colors[i]}, 80%, 70%)`,
      });
    }

    function drawWave() {
      waveCtx!.fillStyle = 'rgba(10, 10, 10, 0.3)';
      waveCtx!.fillRect(0, 0, waveCanvas!.width, waveCanvas!.height);

      const gradient1 = waveCtx!.createLinearGradient(0, 0, waveCanvas!.width, waveCanvas!.height);
      gradient1.addColorStop(0, waveColors[0]);
      gradient1.addColorStop(1, waveColors[0]);

      const gradient2 = waveCtx!.createLinearGradient(waveCanvas!.width, 0, 0, waveCanvas!.height);
      gradient2.addColorStop(0, waveColors[1]);
      gradient2.addColorStop(1, waveColors[1]);

      waveCtx!.globalAlpha = 0.5;

      const waveStep = 5 * scale;

      waveCtx!.fillStyle = gradient1;
      waveCtx!.beginPath();
      waveCtx!.moveTo(0, waveCanvas!.height);

      for (let x = 0; x <= waveCanvas!.width; x += waveStep) {
        const y =
          (waveCanvas!.height / 4) * 3 +
          Math.sin(x * 0.02 + time * 0.5 * waveSpeed) * 50 * scale +
          Math.sin(x * 0.03 + time * 0.75 * waveSpeed) * 15 * scale;
        waveCtx!.lineTo(x, y);
      }

      waveCtx!.lineTo(waveCanvas!.width, waveCanvas!.height);
      waveCtx!.closePath();
      waveCtx!.fill();

      waveCtx!.fillStyle = gradient2;
      waveCtx!.beginPath();
      waveCtx!.moveTo(0, waveCanvas!.height);

      for (let x = 0; x <= waveCanvas!.width; x += waveStep) {
        const y =
          waveCanvas!.height / 2 +
          Math.sin(x * 0.025 + time * 0.5 * waveSpeed + 2) * 25 * scale +
          Math.sin(x * 0.035 + time * 0.6 * waveSpeed) * 10 * scale;
        waveCtx!.lineTo(x, y);
      }

      waveCtx!.lineTo(waveCanvas!.width, waveCanvas!.height);
      waveCtx!.closePath();
      waveCtx!.fill();
    }

    function drawMesh() {
      meshCtx!.fillStyle = 'rgba(20, 20, 20, 0.15)';
      meshCtx!.fillRect(0, 0, meshCanvas!.width, meshCanvas!.height);

      points.forEach((point) => {
        point.x += point.vx;
        point.y += point.vy;

        if (point.x < 0 || point.x > meshCanvas!.width) {
          point.vx *= -1;
        }
        if (point.y < 0 || point.y > meshCanvas!.height) {
          point.vy *= -1;
        }
      });

      const meshStep = 12 * scale;

      for (let x = 0; x < meshCanvas!.width; x += meshStep) {
        for (let y = 0; y < meshCanvas!.height; y += meshStep) {
          let r = 0,
            g = 0,
            b = 0,
            totalWeight = 0;

          points.forEach((point) => {
            const dx = x - point.x;
            const dy = y - point.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const weight = 1 / (dist + 1);

            const hue = parseInt(point.color.match(/\d+/)?.[0] ?? '0');
            const rgb = hslToRgb(hue / 360, 0.7, 0.6);

            r += rgb[0] * weight;
            g += rgb[1] * weight;
            b += rgb[2] * weight;
            totalWeight += weight;
          });

          r /= totalWeight;
          g /= totalWeight;
          b /= totalWeight;

          meshCtx!.fillStyle = `rgb(${r}, ${g}, ${b})`;
          meshCtx!.globalAlpha = 0.4;
          meshCtx!.fillRect(x, y, meshStep, meshStep);
        }
      }
    }

    function animate() {
      drawWave();
      drawMesh();

      if (bobbingIntensity > 0) {
        bobbingTime += 0.05;
        updateBobbingTransform();
      }

      time += 0.01;
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    function hslToRgb(h: number, s: number, l: number): [number, number, number] {
      let r: number, g: number, b: number;

      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p: number, q: number, t: number) => {
          if (t < 0) {
            t += 1;
          }
          if (t > 1) {
            t -= 1;
          }
          if (t < 1 / 6) {
            return p + (q - p) * 6 * t;
          }
          if (t < 1 / 2) {
            return q;
          }
          if (t < 2 / 3) {
            return p + (q - p) * (2 / 3 - t) * 6;
          }
          return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }

      return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }

    animate();

    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    size,
    waveSpeed,
    meshSpeed,
    waveOpacity,
    waveBlur,
    meshBlur,
    bobbingSpeed,
    bobbingIntensity,
    numPoints,
  ]);

  return (
    <div className={styles.loaderWrapper}>
      <div ref={loaderContainerRef} className={classNames(styles.loaderContainer, className)}>
        <canvas ref={waveCanvasRef} className={styles.waveCanvas} />
        <canvas ref={meshCanvasRef} className={styles.meshCanvas} />
      </div>
    </div>
  );
};
